import { useQuery } from "@apollo/client";
import { useRouteMatch } from "react-router-dom";
import {
  GetMeetingFeedbackNewPageQueryQuery,
  GetMeetingFeedbackNewPageQueryQueryVariables,
} from "types/graphql-schema";

import Artifact from "@apps/artifact/artifact";
import getMeetingFeedbackQuery from "@apps/meeting-new/graphql/get-meeting-feedback-query";
import useUiPreferenceCache, {
  UiPreferenceCache,
} from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import Layout from "@components/layout/layout";
import { assertEdgesNonNull } from "@helpers/helpers";

const MeetingFeedbackCollapsible = ({
  expandedUiPreferenceKey,
  title,
  variables,
}: {
  expandedUiPreferenceKey: keyof UiPreferenceCache;
  title: string;
  variables: GetMeetingFeedbackNewPageQueryQueryVariables;
}) => {
  const { url } = useRouteMatch();
  const { uiPreferenceCache } = useUiPreferenceCache();

  const { data, fetchMore, loading } = useQuery<
    GetMeetingFeedbackNewPageQueryQuery,
    GetMeetingFeedbackNewPageQueryQueryVariables
  >(getMeetingFeedbackQuery, {
    notifyOnNetworkStatusChange: true,
    variables: {
      ...variables,
      fetchData: !!uiPreferenceCache[`${expandedUiPreferenceKey}`],
    },
  });

  const handleClickMore = () => {
    fetchMore({
      variables: {
        after: data?.artifacts?.pageInfo.endCursor,
        merge: true,
      },
    });
  };

  const totalCount = data?.artifacts?.totalCount;
  const feedback = data?.artifacts?.edges
    ? assertEdgesNonNull(data.artifacts)
    : [];

  return (
    <Layout.SidebarSubSection
      expandedUiPreferenceKey={expandedUiPreferenceKey}
      title={title}
      loading={feedback.length === 0 && loading}
      count={totalCount}
    >
      <Layout.SidebarSubSectionList
        loading={loading}
        hasNextPage={!!data?.artifacts?.pageInfo.hasNextPage}
        onClickMore={handleClickMore}
      >
        {feedback.map((feedbackItem) =>
          feedbackItem.__typename === "FeedbackArtifactNode" ? (
            <Layout.SidebarSubSectionListItem key={feedbackItem.id}>
              <Artifact artifact={feedbackItem} urlPrefix={url} />
            </Layout.SidebarSubSectionListItem>
          ) : null
        )}
      </Layout.SidebarSubSectionList>
    </Layout.SidebarSubSection>
  );
};

export default MeetingFeedbackCollapsible;
